/**
 * Guest acquisition — shared login path and /kita landing copy by share scenario.
 * Keep thin: same preview UI, different promise continuity from WhatsApp → page → CTA.
 */

import { PARENT_MAIL_LABEL } from "./scheduleLabels";

/**
 * Safe in-app path for login redirect/back. Rejects protocol-relative URLs.
 * @param {string | null | undefined} raw
 * @param {string} [fallback]
 */
export function sanitizeAppPath(raw, fallback = "/") {
  if (typeof raw !== "string") return fallback;
  if (!raw.startsWith("/") || raw.startsWith("//") || raw.startsWith("/\\")) {
    return fallback;
  }
  return raw;
}

/**
 * @param {string | null | undefined} utmScenario
 * @param {{ back?: string | null }} [opts] `back` is the login “Zurück” target (chat), not post-login redirect.
 */
export function guestLoginHref(utmScenario, { back } = {}) {
  const params = new URLSearchParams({ redirect: "/schule" });
  if (utmScenario != null && utmScenario !== "") {
    params.set("utm_scenario", utmScenario);
  }
  const backPath = sanitizeAppPath(back, "");
  if (backPath) params.set("back", backPath);
  return `/login?${params.toString()}`;
}

/** Guest CTAs → login → empty inbox routes to school setup in chat. */
export const LOGIN_SCHULE = guestLoginHref();

/** Chat “Mit einer Mail starten”: after login still /schule; Zurück returns to chat. */
export const LOGIN_SCHULE_FROM_CHAT = guestLoginHref(null, { back: "/" });

/**
 * Login “Zurück” — prefer explicit `back` (chat CTAs). Fall back to redirect.
 * @param {{ redirectTo?: string, back?: string | null }} [opts]
 */
export function loginBackTarget({ redirectTo = "/", back = null } = {}) {
  const fromBack = sanitizeAppPath(back, "");
  if (fromBack) return fromBack;
  const dest = sanitizeAppPath(redirectTo, "/");
  if (dest.startsWith("/login")) return "/";
  return dest;
}

/**
 * Header Anmelden: KiTa guest previews must not return to an empty /schule inbox.
 * @param {string} pathname
 * @param {string} [search]
 */
export function guestAuthLoginHref(pathname, search = "") {
  if (pathname === "/kita" || pathname === "/schule") {
    const params = new URLSearchParams(
      search.startsWith("?") ? search.slice(1) : search,
    );
    return guestLoginHref(params.get("utm_scenario"));
  }
  const currentPath = `${pathname}${search || ""}`;
  return `/login?redirect=${encodeURIComponent(currentPath)}`;
}

/**
 * @typedef {{ title: string, lead: string, cta: string, stepsTitle?: string }} KitaLandingCopy
 */

/** @type {KitaLandingCopy} */
const DEFAULT_LANDING = {
  title: "KiTa & Kindergarten",
  lead: `${PARENT_MAIL_LABEL} weiterleiten oder Flyer fotografieren — Albis macht Aufgabe und Termin kalenderfertig, damit du Fristen nicht im Kopf behalten musst.`,
  cta: "Mit einer Mail starten",
};

/** @type {Record<string, KitaLandingCopy>} */
const LANDING_BY_SCENARIO = {
  kita_peer: {
    title: "KiTa & Kindergarten",
    lead: `${PARENT_MAIL_LABEL}s weiterleiten oder Flyer fotografieren — Albis macht Termine und Fristen kalenderfertig, damit sie nicht untergehen. Genau das, was andere Eltern meinen.`,
    cta: "Mit einer Mail starten",
  },
  kita_partner: {
    title: "KiTa & Kindergarten",
    lead: `Für euch beide: ${PARENT_MAIL_LABEL}s weiterleiten oder Flyer fotografieren — Termine und Fristen kalenderfertig machen, ohne dass einer alles im Kopf behält.`,
    cta: "Bei uns einrichten",
  },
  kita_extract_partner: {
    title: "KiTa & Kindergarten",
    lead: `Die Termine hast du schon — mit Albis kommen die nächsten ${PARENT_MAIL_LABEL}s oder Flyer als Fristen und Erinnerungen.`,
    cta: "Mit einer Mail starten",
  },
};

/**
 * @param {string | null | undefined} utmScenario
 * @returns {KitaLandingCopy}
 */
export function getKitaLandingCopy(utmScenario) {
  if (utmScenario && LANDING_BY_SCENARIO[utmScenario]) {
    return LANDING_BY_SCENARIO[utmScenario];
  }
  return DEFAULT_LANDING;
}

/**
 * Login page: KiTa-intent vs generic household signup.
 * @param {{ redirectTo?: string, utmScenario?: string | null }} opts
 */
export function isKitaLoginIntent({ redirectTo = "/", utmScenario = null } = {}) {
  if (utmScenario) return true;
  if (redirectTo === "/schule" || redirectTo.startsWith("/schule?")) return true;
  if (redirectTo === "/mehr" || redirectTo.startsWith("/mehr?")) return true;
  return false;
}

export const LOGIN_SUBTITLE_KITA =
  "Eltern-Mails weiterleiten oder Flyer fotografieren — Albis merkt Termine und Fristen, damit du sie nicht im Kopf behalten musst.";

export const LOGIN_SUBTITLE_DEFAULT =
  "Melde dich an, um Albis für eure Familie zu nutzen.";
